import React, { useState, useEffect } from 'react';
import {
    Container, Box, Typography, Paper, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Dialog, DialogActions, DialogContent,
    Backdrop, CircularProgress, DialogTitle, TablePagination, Snackbar, Alert, TableSortLabel, MenuItem, DialogContentText, Collapse
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import { Edit, Delete, InfoOutlined, ExpandMore, ExpandLess } from '@mui/icons-material';
import fetchData from '../../components/fetchData';
import getIdToken from '../../components/getIdToken';
import { remoteConfig } from '../../config/firebase';
import { fetchAndActivate, getAll } from "firebase/remote-config";
import { Add } from '@mui/icons-material';

// Backend-owned system settings (GET/PATCH /xeo/admin/settings, manager 4.44.3).
// These are surfaced as rows inside the Remote Config table so admins can find them
// by the same key search and edit them in one place. They are value-only (no key
// rename) and cannot be added or deleted from here.
const SYSTEM_SETTINGS = [
    { key: 'max_unhosted_sites', label: 'Max unhosted (unpublished) sites', type: 'int', unit: '', min: 0 },
    { key: 'hosting_period_minutes', label: 'Hosting cycle length', type: 'int', unit: 'minutes (43200 = 30d)', min: 1, max: 527040 },
    { key: 'grace_period_hours', label: 'Grace period', type: 'int', unit: 'hours', min: 1, max: 720 },
    { key: 'grace_minutes', label: 'Grace minutes', type: 'int', unit: 'minutes', min: 0 },
    { key: 'default_hosting_cost', label: 'Default hosting cost', type: 'number', unit: 'credits', min: 0 },
    { key: 'default_editing_quota', label: 'Default editing quota', type: 'number', unit: 'credits', gtZero: true },
    { key: 'default_article_cost', label: 'Default article cost', type: 'number', unit: 'credits', gtZero: true },
    { key: 'credit_anthropic_rate', label: 'Credit rate', type: 'number', unit: 'USD / credit', gtZero: true, max: 1 },
    { key: 'autotopup_default_threshold', label: 'Auto top-up default threshold', type: 'number', unit: 'credits', gtZero: true },
    { key: 'hosting_billing_enabled', label: 'Hosting billing enabled', type: 'boolean', help: 'Master switch for per-site hosting subscriptions (publish gate, renewal cron, restore).' },
    { key: 'chat_edit_charging_enabled', label: 'Chat-edit charging enabled', type: 'boolean', help: 'Charge credits for AI edits beyond the included quota.' },
    { key: 'publish_decoupling_enabled', label: 'Publish decoupling (unpublish / republish)', type: 'boolean', help: 'Allow taking a live site offline and back without touching billing.' },
];
const SYS_KEYS = SYSTEM_SETTINGS.reduce((m, f) => { m[f.key] = f; return m; }, {});
const sysDisplayValue = (f, v) => {
    if (v == null) return '—';
    if (f.type === 'boolean') return v === true ? 'Enabled' : 'Disabled';
    return f.unit ? `${v} ${f.unit}` : String(v);
};
// Key-search match. A row matches by its real key; system rows also match the word
// "system" and their friendly label — so searching "system" surfaces all of them.
const rowKeyMatches = (config, term) => {
    if (!term) return true;
    const t = term.toLowerCase();
    return (config.searchKey || config.key.toLowerCase()).includes(t);
};
const validateSysNumber = (field, raw) => {
    if (raw === '' || raw == null) return 'Value is required.';
    const n = Number(raw);
    if (!Number.isFinite(n)) return 'Must be a number.';
    if (field.type === 'int' && !Number.isInteger(n)) return 'Must be a whole number.';
    if (field.gtZero && n <= 0) return 'Must be greater than 0.';
    if (field.min != null && n < field.min) return `Must be ≥ ${field.min}.`;
    if (field.max != null && n > field.max) return `Must be ≤ ${field.max}.`;
    return '';
};

const AdminConfigurations = () => {
    const [loading, setLoading] = useState('');
    const [searchKeyTerm, setSearchKeyTerm] = useState('');
    const [searchValueTerm, setSearchValueTerm] = useState('');
    const [configurations, setConfigurations] = useState([]);
    const [filteredConfigurations, setFilteredConfigurations] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [currentRow, setCurrentRow] = useState(null);
    const [datatype, setDatatype] = useState("string");
    const [valueError, setValueError] = useState("");
    const [keyError, setKeyError] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('error');
    const [sortColumn, setSortColumn] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');
    // Backend system settings (merged into the table as read-only-key, edit-only rows).
    const [sysValues, setSysValues] = useState(null);
    const [sysDialogOpen, setSysDialogOpen] = useState(false);
    const [sysEditing, setSysEditing] = useState(null);   // the SYSTEM_SETTINGS meta being edited
    const [sysEditValue, setSysEditValue] = useState('');
    const [sysEditError, setSysEditError] = useState('');
    const [sysSaving, setSysSaving] = useState(false);
    const [sysNoteOpen, setSysNoteOpen] = useState(false); // info note collapsed by default
    const navigate = useNavigate();
    const location = useLocation();

    // Build the merged rows: for each known system setting, a table row keyed by its
    // real key so the existing key/value search finds it. Marked __system so the row
    // renders Edit-only (no delete) and opens the system editor.
    const buildSystemRows = (obj) => SYSTEM_SETTINGS.map((f) => ({
        id: 'sys:' + f.key, key: f.key, value: sysDisplayValue(f, obj ? obj[f.key] : null), __system: true,
        // Extra searchable text so a key search for "system" (or the label) finds it.
        searchKey: `${f.key} ${f.label} system settings`.toLowerCase(),
    }));

    const fetchConfigurations = async () => {
        try {
            // Fetch and activate the latest remote config values
            await fetchAndActivate(remoteConfig);
            setLoading(true);

            // Dynamically fetch all configuration parameters and values
            const allConfigurations = getAll(remoteConfig);

            // Convert the configurations object to an array for easier handling
            const rcConfigurations = Object.keys(allConfigurations).map((key, index) => ({
                id: index + 1,
                key: key,
                value: allConfigurations[key].asString(),
            }));

            // Pull the backend system settings and append them as rows so they live in
            // the same searchable table. Non-fatal if it fails — RC still shows.
            let sysObj = null;
            try {
                const sys = await fetchData({ url: `${process.env.REACT_APP_API_BASE_URL}xeo/admin/settings` });
                if (sys && !sys.error) sysObj = sys;
            } catch (e) { /* ignore — show RC only */ }
            setSysValues(sysObj);

            const updatedConfigurations = [...rcConfigurations, ...buildSystemRows(sysObj)];

            // Get query parameters for search and page
            const queryParams = new URLSearchParams(location.search);
            const searchKeyFromQuery = queryParams.get("searchKey") || "";
            const searchValueFromQuery = queryParams.get("searchValue") || "";

            let filtered;
            // Filter configurations based on the search terms
            if (searchKeyFromQuery || searchValueFromQuery) {
                filtered = updatedConfigurations.filter(config => {
                    const keyMatches = rowKeyMatches(config, searchKeyFromQuery);
                    const valueMatches = !searchValueFromQuery || config.value.toLowerCase().includes(searchValueFromQuery.toLowerCase());
                    return keyMatches && valueMatches;
                });
            } else {
                filtered = updatedConfigurations;
            }

            // Update state with the fetched configurations
            setConfigurations(filtered);
            setFilteredConfigurations(filtered);
        } catch (err) {
            console.error("Error fetching remote config: ", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfigurations();
    }, []);

    const handleReset = () => {
        setSearchKeyTerm('');
        setSearchValueTerm('');
        navigate("/dashboard/configurations?page=1", { replace: true });
        setFilteredConfigurations(configurations);
    };

    const determineDatatype = (value) => {
        setValueError('');
        if (!isNaN(value)) return "number";
        if (value === "true" || value === "false") return "boolean";
        try {
            JSON.parse(value);
            return "JSON";
        } catch {
            return "string";
        }
    };

    const validateValue = (value, type) => {
        const trimmedValue = value ? value.trim() : "";

        if (type === "string" && trimmedValue === "") {
            setValueError("Value must be a non-empty string.");
            return false;
        }
        if (type === "number") {
            if (trimmedValue === "" || isNaN(trimmedValue)) {
                setValueError("Value must be a valid number.");
                return false;
            }
        }
        if (type === "boolean" && !["true", "false"].includes(value) && trimmedValue === "") {
            setValueError("Value must be 'true' or 'false'.");
            return false;
        }
        if (type === "JSON") {
            if (trimmedValue === "") {
                setValueError("Value must be a non-empty JSON string.");
                return false;
            }
            try {
                JSON.parse(trimmedValue);
            } catch {
                setValueError("Value must be a valid JSON string.");
                return false;
            }
        }
        setValueError("");
        return true;
    };

    const renderExample = (type) => {
        switch (type) {
            case "string":
                return "Example: Hello World";
            case "number":
                return "Example: 12345";
            case "boolean":
                return "Example: true or false";
            case "JSON":
                return "Example: [{ },{ }]";
            default:
                return "";
        }
    };

    const closeAppDialog = () => {
        setCurrentRow({ key: " ", value: " " });
        setDatatype("JSON");

    };

    const openAddDialog = () => {
        setCurrentRow({ key: "", value: "" });
        setDatatype("string");
        setValueError("");
        setDialogOpen(true);
    };

    const handleEdit = (id) => {
        const row = configurations.find((config) => config.id === id) || filteredConfigurations.find((config) => config.id === id);
        if (!row) return;
        if (row.__system) { openSysEdit(row.key); return; }
        setCurrentRow(row);
        setDatatype(determineDatatype(row.value));
        setValueError("");
        setDialogOpen(true);
    };

    // ── System-setting editor (value-only; PATCH /xeo/admin/settings) ──
    const backfillDone = sysValues && sysValues.hosting_backfill_completed_at != null && sysValues.hosting_backfill_completed_at !== 0;
    const openSysEdit = (key) => {
        const f = SYS_KEYS[key]; if (!f) return;
        const v = sysValues ? sysValues[key] : null;
        setSysEditing(f);
        setSysEditValue(f.type === 'boolean' ? (v === true ? 'true' : 'false') : (v != null ? String(v) : ''));
        setSysEditError('');
        setSysDialogOpen(true);
    };
    const closeSysEdit = () => { if (!sysSaving) { setSysDialogOpen(false); setSysEditing(null); } };
    const handleSysSave = async () => {
        if (!sysEditing) return;
        let value;
        if (sysEditing.type === 'boolean') {
            value = sysEditValue === 'true';
            if (sysEditing.key === 'hosting_billing_enabled' && value === true && !backfillDone) {
                setSysEditError('Can’t enable until the hosting backfill has completed on the backend.');
                return;
            }
        } else {
            const err = validateSysNumber(sysEditing, sysEditValue);
            if (err) { setSysEditError(err); return; }
            value = Number(sysEditValue);
        }
        setSysSaving(true);
        const resp = await fetchData({ url: `${process.env.REACT_APP_API_BASE_URL}xeo/admin/settings`, method: 'PATCH', body: { [sysEditing.key]: value } });
        setSysSaving(false);
        if (!resp || resp.error || resp.error_code) {
            setSysEditError(resp && resp.error_code === 'backfill_required'
                ? 'Hosting billing can’t be enabled until the hosting backfill has completed.'
                : ((resp && (resp.error || resp.message)) || 'Could not save. Please try again.'));
            return;
        }
        // Prefer the returned object; otherwise patch locally. Then rebuild the merged rows.
        const nextObj = (resp && typeof resp === 'object' && !Array.isArray(resp) && resp[sysEditing.key] !== undefined)
            ? resp : { ...(sysValues || {}), [sysEditing.key]: value };
        setSysValues(nextObj);
        const patchRow = (list) => list.map((r) => r.id === 'sys:' + sysEditing.key ? { ...r, value: sysDisplayValue(sysEditing, nextObj[sysEditing.key]) } : r);
        setConfigurations((prev) => patchRow(prev));
        setFilteredConfigurations((prev) => patchRow(prev));
        setSysDialogOpen(false); setSysEditing(null);
        setSnackbarSeverity('success'); setSnackbarMessage('Setting saved successfully!'); setSnackbarOpen(true);
    };

    const handleSave = async () => {
        let hasError = false;

        const keyRegex = /^[A-Za-z_][A-Za-z0-9_]*$/;
        if (!currentRow?.key || currentRow.key.trim() === "") {
            setKeyError("Key must not be empty.");
            hasError = true;
        } else if (!keyRegex.test(currentRow.key.trim())) {
            setKeyError("Invalid key format. Keys must start with a letter or underscore and contain only letters, numbers, or underscores.");
            hasError = true;
        }

        if (!currentRow?.value || currentRow.value.trim() === "") {
            setValueError("Value must not be empty.");
            hasError = true;
        }

        // Check for duplicate keys, excluding the current row and the merged system rows.
        const isDuplicate = configurations.some(
            (config) =>
                !config.__system &&
                config.key.trim().toLowerCase() === currentRow.key.trim().toLowerCase() &&
                config.id !== currentRow.id
        );

        if (isDuplicate) {
            setKeyError("Key already exists. Please use a unique key.");
            hasError = true;
        }

        if (hasError) return;

        if (!validateValue(currentRow.value, datatype)) return;

        try {
            setLoading(true);

            let oldKey = null;
            let existingRow = null;

            if (currentRow.id) {
                existingRow = configurations.find(config => config.id === currentRow.id);
                if (existingRow && existingRow.key !== currentRow.key) {
                    oldKey = existingRow.key;
                }
            }

            // Update Remote Config first
            const rcResponse = await fetch(`${process.env.REACT_APP_CLOUD_FUNCTION_BASE_URL}setRemoteConfig`, {
                method: "POST",
                headers: { Authorization: `Bearer ${await getIdToken()}`, "Content-Type": "application/json" },
                body: JSON.stringify({
                    key: currentRow.key,
                    value: currentRow.value,
                    oldKey,
                }),
            });
            if (!rcResponse.ok) {
                throw new Error(`Server error: ${await rcResponse.text()}`);
            }

            // Update local state after successful Remote Config update
            const updatedConfigurations = currentRow.id
                ? configurations.map(config =>
                    config.id === currentRow.id ? {
                        ...config,
                        key: currentRow.key,
                        value: currentRow.value
                    } : config
                )
                : [...configurations, {
                    ...currentRow,
                    id: Date.now()
                }];

            setConfigurations(updatedConfigurations);
            setFilteredConfigurations(updatedConfigurations);
            setDialogOpen(false);
        } catch (err) {
            console.error("Error updating remote config: ", err);
            alert("Failed to update configuration. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setCurrentRow(null);
        setKeyError('');
        setValueError('');
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const searchKeyFromQuery = queryParams.get("searchKey") || '';
        const searchValueFromQuery = queryParams.get("searchValue") || '';
        const pageQuery = queryParams.get('page') || '1';

        setSearchKeyTerm(searchKeyFromQuery);
        setSearchValueTerm(searchValueFromQuery);
        setPage(parseInt(pageQuery, 10) - 1);

        fetchConfigurations();
    }, [location.search]);

    const handleSearch = () => {
        const filtered = configurations.filter(config => {
            const keyMatches = rowKeyMatches(config, searchKeyTerm);
            const valueMatches = !searchValueTerm || config.value.toLowerCase().includes(searchValueTerm.toLowerCase());
            return keyMatches && valueMatches;
        });
        setFilteredConfigurations(filtered);
        const queryParams = new URLSearchParams();
        if (searchKeyTerm) queryParams.set("searchKey", searchKeyTerm);
        if (searchValueTerm) queryParams.set("searchValue", searchValueTerm);
        queryParams.set("page", 1);
        navigate({ search: queryParams.toString() });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        const queryParams = new URLSearchParams();
        if (searchKeyTerm) queryParams.set("searchKey", searchKeyTerm);
        if (searchValueTerm) queryParams.set("searchValue", searchValueTerm);
        if (sortColumn) queryParams.set("sortColumn", sortColumn);
        if (sortOrder) queryParams.set("sortOrder", sortOrder);
        queryParams.set('page', newPage + 1);
        navigate({ search: queryParams.toString() });
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        localStorage.setItem('rowsPerPage', parseInt(event.target.value, 10));
        const queryParams = new URLSearchParams();
        if (searchKeyTerm) queryParams.set("searchKey", searchKeyTerm);
        if (searchValueTerm) queryParams.set("searchValue", searchValueTerm);
        if (sortColumn) queryParams.set("sortColumn", sortColumn);
        if (sortOrder) queryParams.set("sortOrder", sortOrder);
        queryParams.set('page', 1);
        navigate({ search: queryParams.toString() });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Sorting Functionality
    useEffect(() => {
        if (configurations.length > 0) {
            const sortedConfigurations = [...configurations].sort((a, b) => {
                const aValue = a[sortColumn] || '';
                const bValue = b[sortColumn] || '';

                if (sortOrder === 'asc') {
                    return aValue.localeCompare(bValue);
                } else {
                    return bValue.localeCompare(aValue);
                }
            });
            setFilteredConfigurations(sortedConfigurations);
        }
    }, [sortColumn, sortOrder, configurations]);

    const handleSort = (column) => {
        const isAsc = sortColumn === column && sortOrder === 'asc';
        const newSortOrder = isAsc ? 'desc' : 'asc';
        setSortColumn(column);
        setSortOrder(newSortOrder);

        // Query Param's Settings
        const queryParams = new URLSearchParams(location.search);
        queryParams.set('sortColumn', column);
        queryParams.set('sortOrder', newSortOrder);
        queryParams.set('page', 1);

        navigate({ search: queryParams.toString() });
    };

    // Delete Functionality
    const handleClickOpen = (key) => {
        setDeleteId(key);
        console.log("Delete ID:", key);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setDeleteId(null);
    };

    const handleDelete = async () => {
        console.log("Delete ID in handleDelete:", deleteId);
        try {
            handleClose();

            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_CLOUD_FUNCTION_BASE_URL}deleteRemoteConfig`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${await getIdToken()}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ key: deleteId }),
            });

            if (!response.ok) {
                // Handle HTTP errors
                const errorMessage = await response.text();
                throw new Error(`Server error: ${errorMessage}`);
            }

            const result = await response.text();
            console.log(result);

            await fetchConfigurations();
            setLoading(false);
        } catch (error) {
            console.error("Error deleting remote config:", error.message);
            alert("Failed to delete remote config. Please try again.");
        }
    };

    // Paginated Configurations
    const paginatedConfigurations = filteredConfigurations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Container maxWidth={false} sx={{ width: '100%', padding: 0, paddingBottom: '5px' }}>
            <Box alignItems="center" sx={{ paddingTop: "10px" }}>
                <Box item xs={4.5} display="flex" justifyContent="space-between">
                    <Typography variant="h5" gutterBottom paddingLeft={"6px"}>
                        Configurations
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={openAddDialog}
                    >
                        Add
                    </Button>
                </Box>
            </Box>

            <Paper elevation={3} sx={{ padding: '20px', marginBottom: '20px', marginTop: '20px' }}>
                <Box sx={{
                    position: 'absolute',
                    backgroundColor: '#D81B60',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '8px',
                    boxShadow: 3,
                }}>
                    <SearchIcon sx={{ fontSize: 30, color: '#fff' }} />
                </Box>
                <Box container spacing={2} alignItems="center">
                    <Box item xs={12} sx={{ paddingLeft: '45px' }}>
                        <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>Search</Typography>
                    </Box>
                </Box>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '16px',
                    marginTop: '15px'
                }}>
                    <TextField
                        value={searchKeyTerm}
                        onChange={(e) => setSearchKeyTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        label="Search by Key"
                        variant="standard"
                        fullWidth
                    />
                    <TextField
                        value={searchValueTerm}
                        onChange={(e) => setSearchValueTerm(e.target.value)}
                        onKeyDown={handleKeyDown}
                        label="Search by Value"
                        variant="standard"
                        fullWidth
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: '16px', marginTop: '20px' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleSearch}
                        style={{ fontWeight: 'bold' }}
                    >
                        Search

                    </Button>
                    <Button
                        variant="outlined"
                        color="secondary"
                        size="large"
                        onClick={handleReset}
                        style={{ fontWeight: 'bold' }}
                    >
                        Reset
                    </Button>
                </Box>
            </Paper>

            {/* Awareness note — collapsed by default. Tells admins that backend "System
                settings" are mixed into this table and how to find/edit them. */}
            <Paper variant="outlined" sx={{ mb: '16px', borderColor: 'rgba(25,118,210,0.35)', backgroundColor: 'rgba(25,118,210,0.045)', borderRadius: 2, overflow: 'hidden' }}>
                <Box onClick={() => setSysNoteOpen((o) => !o)} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, cursor: 'pointer', userSelect: 'none' }}>
                    <InfoOutlined sx={{ color: '#1565c0' }} fontSize="small" />
                    <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 14, color: '#1565c0' }}>
                        This table also includes {SYSTEM_SETTINGS.length} backend “System settings” — how to find &amp; edit them
                    </Typography>
                    {sysNoteOpen ? <ExpandLess sx={{ color: '#1565c0' }} /> : <ExpandMore sx={{ color: '#1565c0' }} />}
                </Box>
                <Collapse in={sysNoteOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ px: 2, pb: 2, pt: 0, color: 'text.secondary', fontSize: 13.5, lineHeight: 1.7 }}>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            <li>Rows tagged with a blue <b>SYSTEM</b> badge are backend-enforced settings (hosting billing, credit rate, grace period, max unhosted sites, and more) — the same values the backend applies.</li>
                            <li>To find them, type <b>“system”</b> in <b>Search by Key</b> to list them all — or search by name, e.g. <b>“hosting”</b>, <b>“credit”</b>, <b>“unhosted”</b>.</li>
                            <li>They are <b>edit-only</b>: you can change the value (number, or Enabled/Disabled), but they can’t be renamed, added, or deleted here.</li>
                        </ul>
                    </Box>
                </Collapse>
            </Paper>

            {loading ? (
                <Backdrop open={true} style={{ color: '#fff', zIndex: 1 }}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            ) : (
                <TableContainer component={Paper} sx={{ marginBottom: '20px' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ fontWeight: 'bold' }}>
                                    <TableSortLabel
                                        active={sortColumn === 'key'}
                                        direction={sortOrder}
                                        onClick={() => handleSort('key')}
                                    >
                                        Key
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell style={{ fontWeight: 'bold' }}>Value</TableCell>
                                <TableCell style={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedConfigurations.map((row) => (
                                <TableRow key={row.id} sx={row.__system ? { backgroundColor: 'rgba(25,118,210,0.035)' } : undefined}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span>{row.key}</span>
                                            {row.__system && (
                                                <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', color: '#1565c0', backgroundColor: 'rgba(25,118,210,0.12)', borderRadius: '6px', padding: '1px 7px' }}>
                                                    SYSTEM
                                                </Box>
                                            )}
                                        </Box>
                                        {row.__system && SYS_KEYS[row.key] && (
                                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: '2px' }}>{SYS_KEYS[row.key].label}</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>{row.value}</TableCell>
                                    <TableCell align="center">
                                        <Button
                                            size="small"
                                            color="success"
                                            onClick={() => handleEdit(row.id)}
                                            startIcon={<Edit />}
                                            style={{ marginLeft: 10, fontWeight: 'bold' }}
                                        >
                                            Edit
                                        </Button>
                                        {!row.__system && (
                                            <Button
                                                size="small"
                                                color="secondary"
                                                onClick={() => handleClickOpen(row.key)}
                                                startIcon={<Delete />}
                                                style={{ marginLeft: 10, fontWeight: "bold" }}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>{currentRow?.id ? "Edit Configuration" : "Add Configuration"}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Key"
                        value={currentRow?.key || ""}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            setCurrentRow({ ...currentRow, key: newValue });
                            setKeyError("");
                        }}
                        fullWidth
                        margin="normal"
                        error={!!keyError}
                        helperText={keyError}
                    />
                    <TextField
                        label="Datatype"
                        select
                        value={datatype}
                        onChange={(e) => {
                            setDatatype(e.target.value);
                            setValueError("");
                        }}
                        fullWidth
                        margin="normal"
                    >
                        <MenuItem value="string">String</MenuItem>
                        <MenuItem value="number">Number</MenuItem>
                        <MenuItem value="boolean">Boolean</MenuItem>
                        <MenuItem value="JSON">JSON</MenuItem>
                    </TextField>
                    <TextField
                        label={`Value`}
                        value={currentRow?.value || ""}
                        multiline
                        onChange={(e) => {
                            setCurrentRow({ ...currentRow, value: e.target.value });
                            setValueError("");
                        }}
                        fullWidth
                        margin="normal"
                        error={!!valueError}
                        helperText={valueError || renderExample(datatype)}
                    />
                </DialogContent>
                <DialogActions style={{ paddingRight: 25, paddingBottom: 25, paddingTop: 0 }}>
                    <Button onClick={handleCloseDialog} color="secondary" variant="outlined" sx={{ fontWeight: "bold" }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} color="primary" variant="outlined" sx={{ fontWeight: "bold" }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ marginTop: 2 }}>
                        Are you sure you want to delete this configuration?
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ paddingRight: 25, paddingBottom: 25, paddingTop: 0 }}>
                    <Button onClick={handleClose} color="secondary" variant="outlined" sx={{ fontWeight: "bold" }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="primary" variant="outlined" sx={{ fontWeight: "bold" }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Handle Apply Actions */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20, overflow: 'hidden', flexDirection: window.innerWidth < 600 ? 'column' : 'row' }}>
                <div style={{ flexGrow: 1 }} />
                <TablePagination
                    component="div"
                    count={filteredConfigurations.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    style={{ paddingBottom: '20px', overflow: 'hidden' }}
                />
            </div>
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={10000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            {/* System-setting editor — opened from the "Edit" on a SYSTEM row. Value-only
                (the key can't be renamed): number field, or Enabled/Disabled select. */}
            <Dialog open={sysDialogOpen} onClose={closeSysEdit} maxWidth="sm" fullWidth>
                <DialogTitle style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>Edit — {sysEditing?.label}</DialogTitle>
                <DialogContent>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1, mb: 1 }}>
                        Key: <code>{sysEditing?.key}</code> · backend-enforced system setting
                    </Typography>
                    {sysEditing?.type === 'boolean' ? (
                        <TextField
                            label="Value" select fullWidth margin="normal"
                            value={sysEditValue}
                            onChange={(e) => { setSysEditValue(e.target.value); setSysEditError(''); }}
                            error={!!sysEditError}
                            helperText={sysEditError || (sysEditing?.help || '')}
                        >
                            <MenuItem value="true">Enabled</MenuItem>
                            <MenuItem value="false">Disabled</MenuItem>
                        </TextField>
                    ) : (
                        <TextField
                            label="Value" type="number" fullWidth margin="normal"
                            value={sysEditValue}
                            onChange={(e) => { setSysEditValue(e.target.value); setSysEditError(''); }}
                            error={!!sysEditError}
                            helperText={sysEditError || (sysEditing?.unit ? `In ${sysEditing.unit}` : '')}
                        />
                    )}
                </DialogContent>
                <DialogActions style={{ paddingRight: 25, paddingBottom: 25, paddingTop: 0 }}>
                    <Button onClick={closeSysEdit} color="secondary" variant="outlined" sx={{ fontWeight: 'bold' }} disabled={sysSaving}>Cancel</Button>
                    <Button onClick={handleSysSave} color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} disabled={sysSaving}>
                        {sysSaving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminConfigurations;